import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Truck, ArrowRight, Fuel, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { buildWhatsAppLink } from "@/components/FloatingWhatsApp";

export default function OrderConfirmation() {
    const { orderNumber } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/orders/by-number/${orderNumber}`)
            .then(r => setOrder(r.data))
            .catch(() => setOrder(false))
            .finally(() => setLoading(false));
    }, [orderNumber]);

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div>;
    }
    if (!order) {
        return <div className="min-h-[60vh] flex flex-col items-center justify-center text-slate-600"><p>Order not found.</p><Link to="/" className="text-blue-700 mt-2">Go home</Link></div>;
    }

    return (
        <div className="bg-slate-50 py-16 min-h-screen" data-testid="order-confirmation-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="bg-white border border-slate-200 rounded-sm p-10 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle2 className="w-9 h-9 text-green-600" />
                    </div>
                    <h1 className="mt-6 font-heading font-black text-4xl md:text-5xl tracking-tighter text-slate-900">Order confirmed!</h1>
                    <p className="mt-3 text-slate-600">We've received your request. Our team will call you within 30 minutes to confirm delivery slot.</p>
                    <div className="mt-8 inline-flex items-center gap-3 bg-slate-900 text-white px-6 py-3 rounded-sm">
                        <span className="text-xs uppercase tracking-widest text-slate-400 font-mono-plex">Reference</span>
                        <span className="font-heading font-black text-xl tab-num text-yellow-400" data-testid="confirmation-order-number">{order.order_number}</span>
                    </div>
                </div>

                <div className="mt-8 bg-white border border-slate-200 rounded-sm p-8">
                    <h2 className="font-heading font-bold text-slate-900 tracking-tight mb-6 flex items-center gap-2"><Fuel className="w-4 h-4 text-blue-700" /> Order Details</h2>
                    <div className="grid sm:grid-cols-2 gap-y-4 gap-x-8 text-sm">
                        <Row k="Contact" v={order.contact_name} />
                        <Row k="Phone" v={order.contact_phone} />
                        <Row k="Quantity" v={`${order.quantity_litres} litres`} />
                        <Row k="Rate / litre" v={`₹${order.price_per_litre.toFixed(2)}`} />
                        <Row k="Delivery Fee" v={order.delivery_fee === 0 ? "FREE" : `₹${order.delivery_fee.toFixed(2)}`} />
                        <Row k="Total (Incl. Tax)" v={<span className="text-blue-700 font-bold">₹{order.total.toFixed(2)}</span>} />
                        <Row k="Delivery Date" v={order.preferred_date} />
                        <Row k="Slot" v={order.preferred_slot} />
                        <Row k="Payment" v={order.payment_method} />
                        <Row k="Status" v={<span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-widest rounded-sm">{order.status}</span>} />
                        <div className="sm:col-span-2">
                            <Row k="Delivery Address" v={`${order.delivery_address}${order.landmark ? `, ${order.landmark}` : ""}, ${order.city} — ${order.pincode}`} />
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <a
                        href={buildWhatsAppLink(
                            `Hi Vaishali Fuels, I've just placed order ${order.order_number} for ${order.quantity_litres} L on ${order.preferred_date} (${order.preferred_slot}). Please confirm the delivery slot. Contact: ${order.contact_name} · ${order.contact_phone}.`
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="confirmation-whatsapp-btn"
                    >
                        <Button className="rounded-sm bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold uppercase tracking-wider">
                            <MessageCircle className="mr-2 w-4 h-4" /> Confirm on WhatsApp
                        </Button>
                    </a>
                    <Link to="/">
                        <Button variant="outline" className="rounded-sm border-slate-300" data-testid="confirmation-home-btn">Back to Home</Button>
                    </Link>
                    <Link to="/order">
                        <Button className="rounded-sm bg-blue-700 hover:bg-blue-800" data-testid="confirmation-new-order-btn">
                            <Truck className="mr-2 w-4 h-4" /> Place Another Order
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Row({ k, v }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-plex">{k}</div>
            <div className="mt-1 text-slate-900">{v}</div>
        </div>
    );
}
